import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { FetchStatusEnum, FetchStatusStrings } from "../../enums/fetchStatusEnum";
import PasswordInput from "../../components/inputs/PasswordInput";
import i18nStore from "../../i18n/store";
import ErrorMsg from "../../ui/ErrorMsg";
import { SignInRequestSchema } from "../../api/schemas/requests/auth";
import { TokenPair } from "../../api/schemas/responses/auth";
import { ACCESS_TOKEN_STORAGE_NAME, REFRESH_TOKEN_STORAGE_NAME } from "../../api/config";
import authUserStore from "./store";
import { API } from "../../api/api";
import { forgotPasswordPath, registrationPath } from "../../routes/paths";
import { MeSchema } from "../../api/schemas/responses/users";
import Grid from "@mui/material/Grid2";

function AuthSignInForm() {
    const navigate = useNavigate();
    const translations = i18nStore.getTranslations;

    const [emailOrLogin, setEmailOrLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");

    const [errorText, setErrorText] = useState<string>(translations.errWrongCredentialsMsg);
    const [actionStatus, setActionStatus] = useState<FetchStatusStrings>(FetchStatusEnum.IDLE);

    useEffect(() => {
        setActionStatus(FetchStatusEnum.IDLE);
    }, [emailOrLogin, password]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setActionStatus(FetchStatusEnum.FETCHING);
        const signInRequestData: SignInRequestSchema = {
            email_or_login: emailOrLogin,
            password: password,
        };

        API.auth
            .signIn(signInRequestData)
            .then((tokens: TokenPair) => {
                setActionStatus(FetchStatusEnum.SUCCESS);
                localStorage.setItem(ACCESS_TOKEN_STORAGE_NAME, tokens.access);
                localStorage.setItem(REFRESH_TOKEN_STORAGE_NAME, tokens.refresh);

                API.users.getMe().then((user: MeSchema) => {
                    authUserStore.update(user);
                });
            })
            .catch((err) => {
                setActionStatus(FetchStatusEnum.ERROR);
                const statusCode = err.status || err.response?.status || 500;
                if (statusCode >= 500) {
                    setErrorText(translations.errServiceUnavailableMsg);
                } else if (statusCode >= 400) {
                    setErrorText(translations.errWrongCredentialsMsg);
                }
            });
    };

    return (
        <Container component="main" maxWidth="xs" sx={{ p: { xs: 0, md: 2, lg: 3} }}>
            <CssBaseline />
            <Card
                sx={{
                    p: { xs: 1, md: 2, lg: 3},
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography component="h1" variant="h5">
                    {translations.signIn}
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <TextField
                        value={emailOrLogin}
                        onChange={(e) => setEmailOrLogin(e.target.value)}
                        margin="normal"
                        fullWidth
                        id="emailOrLogin"
                        label="Email / login"
                        name="emailOrLogin"
                        autoComplete="email"
                    />

                    <PasswordInput password={password} setPassword={setPassword} />

                    <ErrorMsg errText={errorText} visible={actionStatus === FetchStatusEnum.ERROR} />

                    <LoadingButton
                        type="submit"
                        fullWidth
                        loading={actionStatus === FetchStatusEnum.FETCHING}
                        onClick={(e) => handleSubmit(e)}
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={!emailOrLogin || !password}
                    >
                        {translations.signIn}
                    </LoadingButton>
                    <Grid container sx={{ justifyContent: "space-between"}}>
                        <Grid>
                            <Link
                                href={forgotPasswordPath}
                                variant="body1"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(forgotPasswordPath);
                                }}
                            >
                                {translations.forgotPassword}?
                            </Link>
                        </Grid>
                        <Grid>
                            <Link
                                href={registrationPath}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(registrationPath);
                                }}
                                variant="body1"
                                sx={{
                                    "&:hover": {
                                        cursor: "pointer",
                                    },
                                }}
                            >
                                {translations.signUp}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </Container>
    );
}

export default observer(AuthSignInForm);
